<template>
  <Transition name="fade" mode="out-in">
    <div v-if="usingWorksomeTimesheet">
      <ModalTimesheet
        :hire-id="store.hireGlobalId"
        :worker-id="store.workerId"
        :is-open="timesheetOpen"
        :timesheet-id="timesheetsStore.worksomeTimesheetId"
        :start-date="store.paymentRequestForm.dateStart"
        :end-date="store.paymentRequestForm.dateEnd"
        :rate-type="store.paymentRequestRate.rateType"
        :job-name="store.jobName"
        :company-name="store.companyName"
        :detailed-view="store.requiresStartAndEndTimesOnTimesheets"
        :readonly="store.isEditable === false"
        :enable-timesheet-lines="store.enableWorksomeTimesheetLinesFeature"
        :has-required-timesheet-line-custom-fields="hasRequiredTimesheetLineCustomFields"
        :open-file-picker-on-mount="shouldOpenFilePicker"
        :enable-ai-timesheet-parsing="store.enableAiTimesheetParsing"
        auto-load-timesheet-data
        @updated-timesheet="handleUpdatedTimesheet"
        @cancel="handleCancel"
      ></ModalTimesheet>
      <ModalTimesheetConfirmationDelete
        :show="isOpenRemoveTimesheetDialog"
        :working="deletingTimesheet"
        @cancel="isOpenRemoveTimesheetDialog = false"
        @delete-timesheet="deleteWorksomeTimesheet"
      />
      <div
        v-if="!timesheetsStore.worksomeTimesheetId"
        class="tw-flex tw-w-full tw-justify-between tw-mt-sm tw-gap-sm md:tw-gap-md tw-flex-col md:tw-flex-row"
      >
        <div class="tw-flex-1">
          <WdsAlert v-if="store.enableAiTimesheetParsing" info bordered>
            <template #description>
              <div class="tw-flex tw-justify-between tw-items-center tw-mb-sm">
                <WdsStack direction-row gap-sm>
                  <span class="tw-text-indigo-500 tw-text-lg tw-font-medium">
                    {{ $t('Save time with AI-powered Auto-fill!') }}
                  </span>
                </WdsStack>
                <ParserHelpLink />
              </div>
              {{
                $t(
                  "If you already have existing timesheet files (.jpg or .pdf) you can upload them here and let AI do the heavy lifting. We'll auto-fill your hours - you just review and confirm.",
                )
              }}
            </template>
          </WdsAlert>
        </div>
        <div class="tw-flex tw-gap-sm tw-flex-shrink-0 tw-self-end md:tw-self-start">
          <div>
            <WdsButton
              default
              class="tw-items-center tw-justify-center tw-whitespace-nowrap"
              data-test="timesheet-create-button"
              @click="openTimesheet"
            >
              <WdsIcon :size="16" name="plus" />
              <div class="tw-ml-2">{{ $t('Create timesheet') }}</div>
            </WdsButton>
          </div>
          <div>
            <WdsButton
              v-if="store.enableAiTimesheetParsing"
              class="worksome-intelligence-border tw-whitespace-nowrap"
              lg
              secondary
              data-test="timesheet-parsing-upload-button"
              @click="openFilePicker"
            >
              <WdsStack align-center gap-sm>
                <WdsIcon :name="WdsIconNameEnum.WORKSOME_ASSISTANT_GRADIENT" xs :size="16" />
                <span>{{ $t('Auto-fill') }}</span>
                <WdsIcon :name="WdsIconNameEnum.UPLOAD" :size="16" />
              </WdsStack>
            </WdsButton>
          </div>
        </div>
      </div>

      <template v-if="timesheetsStore.worksomeTimesheetId">
        <div
          class="tw-flex tw-w-full tw-justify-end tw-mt-md"
          :class="{
            'tw-justify-between': timesheetParsingStore.filesToAttachToPaymentRequest.length > 0,
          }"
        >
          <div v-if="timesheetParsingStore.filesToAttachToPaymentRequest.length > 0">
            <span class="tw-flex tw-items-center tw-gap-sm tw-mb-sm">
              <WdsIcon name="attachment" :size="16" />
              {{ $t('Attachments') }}:
            </span>
            <ProvideApiFilebank v-slot="{ deleteFiles: fileDeleter, deleting }">
              <div class="tw-flex tw-flex-col tw-gap-sm">
                <div
                  v-for="file in timesheetParsingStore.filesToAttachToPaymentRequest"
                  :key="file.localId"
                  class="tw-flex tw-items-center tw-gap-sm"
                >
                  <a
                    :href="laravelNamedRoute(namedRoutes.files.download, { fileId: file.localId })"
                    target="_blank"
                  >
                    {{ file.name }}
                  </a>
                  <WdsButtonGhost
                    :disabled="deleting"
                    @click="removeFile(file.localId, fileDeleter)"
                  >
                    <WdsIcon name="trash-can" :size="16" />
                  </WdsButtonGhost>
                </div>
              </div>
            </ProvideApiFilebank>
          </div>
          <div class="tw-w-[200px] tw-max-w-full tw-flex tw-flex-col tw-gap-md">
            <div class="tw-flex tw-items-center tw-justify-between tw-gap-sm">
              <WdsButton
                secondary
                class="tw-items-center tw-justify-center tw-w-full"
                data-test="timesheet-edit-button"
                @click="timesheetOpen = true"
              >
                {{ $t('Timesheet attached') }}
              </WdsButton>
              <WdsButtonGhost
                v-if="canDeleteTimesheet"
                data-test="timesheet-delete-button"
                @click="isOpenRemoveTimesheetDialog = true"
              >
                <WdsIcon name="trash-can" :size="16" />
              </WdsButtonGhost>
            </div>
            <div class="tw-flex tw-flex-col tw-gap-2">
              <div class="tw-flex tw-justify-between">
                <WdsText large>{{ $t('Total payable time') }}</WdsText>
                <WdsText large data-test="total-payable-time-from-timesheet">
                  {{ formatFixedDecimal(timesheetFormStore.totalPayableTime) }}
                  {{
                    store.paymentRequestRate.rateType === RateType.DAILY &&
                    timesheetFormStore.useDayInputs
                      ? $t('d (day abbreviation)')
                      : $t('h (hour abbreviation)')
                  }}
                </WdsText>
              </div>
              <div
                v-if="timesheetFormStore.associatedOvertimes.length > 0"
                class="tw-flex tw-justify-between"
              >
                <WdsText>{{ $t('Regular hours') }}</WdsText>
                <WdsText data-test="regular-time-from-timesheet">
                  {{ formatFixedDecimal(timesheetFormStore.totalPayableTimeMinusOvertime) }}
                  {{
                    store.paymentRequestRate.rateType === RateType.DAILY &&
                    timesheetFormStore.useDayInputs
                      ? $t('d (day abbreviation)')
                      : $t('h (hour abbreviation)')
                  }}
                </WdsText>
              </div>
              <div
                v-for="associatedOvertime in timesheetFormStore.associatedOvertimes"
                :key="associatedOvertime.rate_multiplier"
                class="tw-flex tw-justify-between"
              >
                <div class="tw-flex tw-gap-1 tw-items-center">
                  <WdsText>{{ $t('Overtime') }}</WdsText>
                  <WdsTag sm>
                    {{ formatFixedDecimal(associatedOvertime.rate_multiplier) }} x rate
                  </WdsTag>
                  <WdsTooltip v-if="associatedOvertime.contributingPaymentRequests.size > 1" left>
                    <i class="zmdi zmdi-help" />
                    <template #tooltip-content>
                      {{
                        $t(
                          'This overtime has been calculated from time submitted across multiple payment requests:',
                        )
                      }}
                      <span
                        v-for="contributingPaymentRequest in associatedOvertime.contributingPaymentRequests"
                        :key="contributingPaymentRequest"
                      >
                        #{{ contributingPaymentRequest }}
                      </span>
                    </template>
                  </WdsTooltip>
                </div>
                <WdsText>
                  {{ formatFixedDecimal(associatedOvertime.duration) }}
                  {{
                    store.paymentRequestRate.rateType === RateType.DAILY &&
                    timesheetFormStore.useDayInputs
                      ? $t('d (day abbreviation)')
                      : $t('h (hour abbreviation)')
                  }}
                </WdsText>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div v-else-if="usingLegacyTimesheet" class="tw-relative tw-mb-[40px] tw-mt-lg">
      <Spreadsheet
        :disabled="disabled"
        :model-value="currentTimesheet"
        :ratetype="store.paymentRequestRate.rateType"
        @input="updateTimesheetJson"
      ></Spreadsheet>
    </div>
    <div v-else-if="usingFreetextTimesheet" class="tw-mt-lg">
      <WdsInputArea
        v-model="timesheetsStore.timesheetComment"
        data-test="timesheet-text-input"
        :disabled="disabled"
        :placeholder="$t('Report your time here...')"
        :error="errorMessage"
      ></WdsInputArea>
    </div>
    <div v-else-if="usingAttachedFileTimesheet" class="tw-mt-lg">
      <BaseFileAttachmentFromOs
        v-model="timesheetsStore.timesheetFiles"
        :disabled="disabled"
        :open-on-mounted="shouldOpenFileSelectorImmediately"
        :default-tags="[$t('Timesheet')]"
        button-class="btn btn-default btn-xs"
      />
    </div>
  </Transition>
  <Transition name="fade" mode="out-in">
    <WdsAlert v-if="showTimeConfirmationInput" warning class="tw-mt-6" bordered>
      <div v-if="store.requiresStartAndEndTimesOnTimesheets" class="tw-mb-sm">
        <div class="tw-mb-sm">
          {{ $t('By submitting this timesheet, I confirm that:') }}
        </div>
        <ul class="tw-list-disc tw-pl-lg">
          <li>
            {{
              $t(
                'I have accurately recorded all hours worked, and any overtime worked was approved in advance.',
              )
            }}
          </li>
          <li>
            {{
              $t(
                'I was provided the opportunity to take all required meal breaks and rest breaks required by the law of the state in which I performed work during this pay period.',
              )
            }}
          </li>
          <li>
            {{
              $t(
                'Any meal break I took was uninterrupted and off-duty for its full duration, and I did not perform any work during any unpaid meal break or rest break.',
              )
            }}
          </li>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <li v-html="reportingClauseHtml" />
        </ul>
      </div>
      <WdsCheckbox
        v-model:checked="store.paymentRequestForm.timesheetConfirmed"
        data-test="confirm-timesheet"
        @update:checked="validationStore?.v$?.timesheetConfirmed.$touch()"
      >
        <span v-if="store.requiresStartAndEndTimesOnTimesheets">
          {{ $t('I agree to the above attestation.') }}
        </span>
        <span v-else>
          {{ $t('I confirm that the hours submitted in this timesheet are correct.') }}
        </span>
      </WdsCheckbox>
    </WdsAlert>
  </Transition>
  <Transition name="fade" mode="out-in">
    <WdsText
      v-if="validationStore?.v$?.timesheetConfirmed.$errors?.[0]?.$message"
      error
      class="tw-mt-2"
    >
      {{ validationStore?.v$?.timesheetConfirmed?.$errors?.[0]?.$message }}
    </WdsText>
  </Transition>
</template>

<script lang="ts" setup>
import {
  WdsAlert,
  WdsButton,
  WdsButtonGhost,
  WdsCheckbox,
  WdsIcon,
  WdsIconNameEnum,
  WdsInputArea,
  WdsStack,
  WdsTag,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { computed, nextTick, ref, watch } from 'vue'
import moment from 'moment/moment'
import { useMutation } from '@vue/apollo-composable'
import formatFixedDecimal from '../Timesheet/utilities/formatFixedDecimal'
import Spreadsheet from '@/components/PaymentRequest/Spreadsheet.vue'
import BaseFileAttachmentFromOs from '@/components/BaseFileAttachmentFromOs.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { TimesheetTypeEnum } from '@/components/PaymentRequest/enums'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useValidationStore } from '@/components/PaymentRequest/store/useValidationStore'
import { useTranslation } from '@/composables/translation'
import ModalTimesheet from '@/components/Timesheet/ModalTimesheet.vue'
import ModalTimesheetConfirmationDelete from '@/components/PaymentRequest/ModalTimesheetConfirmationDelete.vue'
import { RateType } from '@/enums'
import { graphql } from '@/gql'
import { useHtmlFormStore } from '@/components/PaymentRequest/store/useHtmlFormStore'
import { useTimesheetParsingStore } from '@/components/Timesheet/store/useTimesheetParsingStore'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import ProvideApiFilebank from '@/components/ProvideApiFilebank.vue'
import ParserHelpLink from '@/components/Timesheet/ParserHelpLink.vue'

const props = withDefaults(
  defineProps<{
    timesheetType: TimesheetTypeEnum
    disabled?: boolean
    hasRequiredTimesheetLineCustomFields?: boolean
  }>(),
  {
    disabled: false,
    hasRequiredTimesheetLineCustomFields: false,
  },
)

const { $t } = useTranslation()
const store = usePaymentRequestStore()
const timesheetsStore = useTimesheetStore()
const timesheetFormStore = useTimesheetFormStore()
const validationStore = useValidationStore()
const htmlFormStore = useHtmlFormStore()
const timesheetParsingStore = useTimesheetParsingStore()
const timesheetOpen = ref(false)

const reportingClauseHtml = computed(() => {
  const link = `<a href="https://help.worksome.dk/en/articles/14752737-us-timesheet-break-reporting" target="_blank" rel="noopener noreferrer" class="tw-underline">${$t('reporting process')}</a>`
  return $t(
    "If I was unable to take any required meal or rest break, or if I performed any work during an unpaid break, I will report it through Worksome's :link.",
    { link },
  )
})

const usingWorksomeTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.TIMESHEET)
const usingLegacyTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.SHEET)
const usingFreetextTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.TEXT)
const usingAttachedFileTimesheet = computed(() => props.timesheetType === TimesheetTypeEnum.FILE)

const currentTimesheet = computed(() => {
  let json = [[moment().format('YYYY-MM-DD'), '', '']]
  try {
    json = JSON.parse(timesheetsStore?.timesheetSpreadsheetValue)
  } catch (exception) {
    //
  }
  return json
})
const updateTimesheetJson = (json) => {
  // BaseTimesheet emits input with updated json string, OR on actual input
  // We don't want to handle native input events
  if (typeof json === 'string') {
    timesheetsStore.timesheetSpreadsheetValue = json
  }
}

const shouldOpenFileSelectorImmediately = ref(false)
watch(
  () => props.timesheetType,
  (newValue, oldValue) => {
    shouldOpenFileSelectorImmediately.value =
      oldValue !== TimesheetTypeEnum.FILE &&
      newValue === TimesheetTypeEnum.FILE &&
      timesheetsStore.timesheetFiles.length === 0
  },
)

const isOpenRemoveTimesheetDialog = ref(false)

const { mutate: deleteTimesheet, loading: deletingTimesheet } = useMutation(
  graphql(/* GraphQL */ `
    mutation deleteTimesheet($input: DeleteTimesheetInput!) {
      deleteTimesheet(input: $input) {
        id
      }
    }
  `),
  () => ({
    variables: {
      input: {
        id: timesheetsStore.worksomeTimesheetId,
      },
    },
    clientId: 'publicApi',
  }),
)

const deleteWorksomeTimesheet = async () => {
  await deleteTimesheet()
  timesheetFormStore.rollbackTimesheetRegistrations()
  timesheetFormStore.clearTimesheet()
  isOpenRemoveTimesheetDialog.value = false
  timesheetsStore.worksomeTimesheetId = ''
  store.paymentRequestForm.timesheetConfirmed = false
}
// If editing an existing payment request's timesheet, we must auto-save the payment request
// so that the payment request totals are updated on backend since a user could potentially
// exit the payment request before saving it themselves, leaving PR and Timesheet out of sync
const shouldAutoSavePaymentRequestOnTimesheetUpdates = computed(
  () => store.paymentRequestId && store.isEditable,
)
const shouldOpenFilePicker = ref(false)
const handleUpdatedTimesheet = async (timesheetId) => {
  timesheetsStore.worksomeTimesheetId = timesheetId
  if (shouldAutoSavePaymentRequestOnTimesheetUpdates.value) {
    if (!store.isDraft) {
      store.paymentRequestForm.timesheetConfirmed = true
    }
    await nextTick()
    htmlFormStore.submitForm(
      // @ts-ignore
      store.isDraft ? '/bills/draft' : `/bills?redirect_uri=/bills/${store.paymentRequestId}/edit`,
    )
  } else {
    timesheetOpen.value = false
    shouldOpenFilePicker.value = false
    store.paymentRequestForm.timesheetConfirmed = false
  }
}
// We only allow deletion of timesheets if the payment request is editable
// and the payment request has not yet been created. Otherwise, the payment request total will
// still have the amount of hours as the deleted timesheet
const canDeleteTimesheet = computed(() => store.isEditable && !store.paymentRequestId)

const showTimeConfirmationInput = computed(
  () =>
    store.isEditable &&
    timesheetsStore.requireTimesheetConfirmation &&
    props.timesheetType !== TimesheetTypeEnum.NONE,
)

const removeFile = async (
  fileId: string | number,
  fileDeleter: (ids: (string | number)[]) => Promise<any>,
): Promise<void> => {
  await fileDeleter([fileId])
  timesheetParsingStore.uploadedFiles = timesheetParsingStore.filesToAttachToPaymentRequest.filter(
    (file) => file.localId !== fileId,
  )
}

const handleCancel = () => {
  timesheetOpen.value = false
  shouldOpenFilePicker.value = false
}

const validateBillingDates = (): boolean => {
  validationStore.v$.dateStart.$touch()
  validationStore.v$.dateEnd.$touch()

  if (validationStore.v$.dateStart.$invalid || validationStore.v$.dateEnd.$invalid) {
    nextTick(() => {
      document
        .querySelector('[name="date_start"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    return false
  }

  return true
}

const openTimesheet = () => {
  if (!validateBillingDates()) return
  shouldOpenFilePicker.value = false
  timesheetOpen.value = true
}

const openFilePicker = () => {
  if (!validateBillingDates()) return
  shouldOpenFilePicker.value = true
  timesheetOpen.value = true
}

const errorMessage = computed(() => {
  const message = validationStore?.v$?.timesheetComment.$errors?.[0]?.$message
  return typeof message === 'object' && message !== null ? message.value : message
})
</script>

<style scoped>
.worksome-intelligence-border {
  position: relative;
  border: none !important;
}

.worksome-intelligence-border::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 4px;
  padding: 1px;
  background: linear-gradient(
    53.76deg,
    #634cee 9.2%,
    rgba(99, 76, 238, 0.94) 26.8%,
    #f15c30 60.32%,
    #ffc800 93.01%
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}
</style>
